import React, { useState, useEffect } from 'react';
import { 
  FaHeart, 
  FaShoppingCart, 
  FaUser, 
  FaSearch, 
  FaBars, 
  FaTimes,
  FaMapMarkerAlt,
  FaHome,
  FaTruck,
  FaUserPlus,
  FaStore,
  FaClock,
  FaFire,
  FaStar
} from 'react-icons/fa';
import { Link, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { name: 'HOME', path: '/' },
    { name: 'EYEGLASSES', path: '/eyeglasses' },
    { name: 'SUNGLASSES', path: '/products' },
    { name: 'CONTACTS', path: '/contact' },
    { name: 'SPECIAL POWER', path: '/special-power' },
    { name: 'STORES', path: '/stores' },
    { name: 'TRY @ HOME', path: '/try-at-home' }
  ];

  return (
    <nav className={`sticky top-0 z-50 transition-all duration-300 ${
      scrolled ? 'bg-black/90 backdrop-blur-md shadow-2xl' : 'bg-black'
    }`}>
      {/* Top Announcement Bar - Fixed for mobile overflow */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white py-1.5 sm:py-2 px-2 sm:px-4 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,white,transparent)]"></div>
        </div>
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-1 sm:gap-2 text-[10px] xs:text-xs sm:text-sm flex-wrap">
          <FaFire className="text-yellow-300 animate-pulse text-xs sm:text-sm" />
          <span className="truncate max-w-[200px] xs:max-w-none xs:whitespace-normal text-center">
            EYE TEST & HOME TRIAL
          </span>
          <FaStar className="text-yellow-300 animate-spin-slow text-xs sm:text-sm" />
        </div>
      </div>

      {/* Main Navbar */}
      <div className="relative border-b border-orange-500/20">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16 lg:h-20">
            {/* Logo - added max-width to prevent overflow */}
            <Link to="/" className="flex-shrink-0 flex items-center group max-w-[120px] xs:max-w-[150px]">
              <div className="relative">
                <div className="absolute -inset-2 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-500 animate-pulse"></div>
                <img 
                  src="/logo1.png" 
                  className="relative h-1 xs:h-10 sm:h-12 lg:h-16 w-auto object-contain"
                />
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center justify-center flex-1 mx-4">
              <div className="flex items-center space-x-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`relative px-3 py-2 rounded-lg text-[11px] xl:text-xs font-medium transition-all duration-300 group ${
                      isActive(link.path)
                        ? 'text-orange-500'
                        : 'text-gray-300 hover:text-white'
                    }`}
                  >
                    <span className="relative z-10">{link.name}</span>
                    <span className={`absolute inset-0 bg-orange-500/10 rounded-lg transform scale-0 group-hover:scale-100 transition-transform duration-300 ${
                      isActive(link.path) ? 'scale-100' : ''
                    }`}></span>
                    {isActive(link.path) && (
                      <span className="absolute -bottom-1 left-2 right-2 h-0.5 bg-gradient-to-r from-orange-500 to-orange-400 rounded-full animate-width"></span>
                    )}
                  </Link>
                ))}
              </div>
            </div>

            {/* Desktop Right Icons */}
            <div className="hidden lg:flex items-center space-x-2 flex-shrink-0">
              <div className="relative group">
                <input
                  type="text"
                  placeholder="Search..."
                  className="w-40 xl:w-48 px-3 py-2 pl-9 bg-gray-900/50 border border-gray-800 rounded-full text-sm text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500/50 transition-all duration-300 group-hover:border-gray-700"
                />
                <FaSearch className="absolute left-3 top-3 h-4 w-4 text-gray-500 group-hover:text-orange-500 transition-colors duration-300" />
              </div>
              {[
                { icon: <FaHeart />, color: 'text-orange-500', hover: 'hover:text-red-500' },
                { icon: <FaShoppingCart />, color: 'text-orange-500', hover: 'hover:text-orange-400' }
              ].map((item, index) => (
                <button
                  key={index}
                  className="relative p-2.5 rounded-full bg-gray-900/50 border border-gray-800 text-gray-400 hover:text-white hover:border-orange-500 hover:bg-orange-500/10 transition-all duration-300 transform hover:scale-110 group"
                >
                  <span className="relative z-10">{item.icon}</span>
                  <span className="absolute inset-0 bg-orange-500/20 rounded-full transform scale-0 group-hover:scale-100 transition-transform duration-300"></span>
                </button>
              ))}
              <div className="relative">
                <button 
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className={`relative p-2.5 rounded-full bg-gray-900/50 border ${
                    isUserMenuOpen ? 'border-orange-500 text-orange-500' : 'border-gray-800 text-gray-400'
                  } hover:text-white hover:border-orange-500 hover:bg-orange-500/10 transition-all duration-300 transform hover:scale-110 group`}
                >
                  <FaUser className="h-4 w-4" />
                </button>
                {isUserMenuOpen && (
                  <>
                    <div 
                      className="fixed inset-0 z-40"
                      onClick={() => setIsUserMenuOpen(false)}
                    />
                    <div className="absolute right-0 mt-3 w-56 bg-black border border-gray-800 rounded-xl shadow-2xl py-2 z-50 transform origin-top-right animate-slideDown">
                      {[
                        { icon: <FaUserPlus />, text: 'Sign Up / Sign In', to: '/signin' },
                        { icon: <FaTruck />, text: 'Track Order', to: '/track-order' },
                        { icon: <FaMapMarkerAlt />, text: 'Locate Store', to: '/locate-store' },
                      ].map((item, index) => (
                        <Link
                          key={index}
                          to={item.to}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-300 hover:text-white hover:bg-orange-500/10 transition-all duration-300 group"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <span className="text-orange-500 group-hover:scale-110 transition-transform">
                            {item.icon}
                          </span>
                          <span>{item.text}</span>
                        </Link>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Mobile Icons - now using mobile-icon class for tiny screens */}
            <div className="flex items-center space-x-1 lg:hidden">
              <button
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                className="p-2 rounded-lg bg-gray-900/50 border border-gray-800 text-gray-400 hover:text-white hover:border-orange-500 hover:bg-orange-500/10 transition-all duration-300 mobile-icon"
              >
                <FaSearch className="h-4 w-4" />
              </button>
              <button className="p-2 rounded-lg bg-gray-900/50 border border-gray-800 text-gray-400 hover:text-white hover:border-orange-500 hover:bg-orange-500/10 transition-all duration-300 mobile-icon">
                <FaHeart className="h-4 w-4" />
              </button>
              <button className="p-2 rounded-lg bg-gray-900/50 border border-gray-800 text-gray-400 hover:text-white hover:border-orange-500 hover:bg-orange-500/10 transition-all duration-300 mobile-icon">
                <FaShoppingCart className="h-4 w-4" />
              </button>
              <button
                onClick={() => setIsMenuOpen(true)}
                className="p-2 rounded-lg bg-gray-900/50 border border-gray-800 text-gray-400 hover:text-white hover:border-orange-500 hover:bg-orange-500/10 transition-all duration-300 mobile-icon"
              >
                <FaBars className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Mobile Search Bar */}
          <div className={`lg:hidden overflow-hidden transition-all duration-500 ${isSearchOpen ? 'max-h-20 pb-3' : 'max-h-0'}`}>
            <div className="relative">
              <input
                type="text"
                placeholder="Search glasses, contacts..."
                className="w-full px-4 py-2.5 pl-10 bg-gray-900/50 border border-gray-800 rounded-xl text-sm text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500/50"
              />
              <FaSearch className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div className={`fixed inset-0 bg-black z-50 lg:hidden transition-transform duration-500 ease-in-out transform ${
        isMenuOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        <div className="h-full overflow-y-auto">
          {/* Menu Header */}
          <div className="sticky top-0 bg-black border-b border-gray-800 px-4 py-3 flex justify-between items-center">
            <button 
              onClick={() => setIsMenuOpen(false)}
              className="p-2.5 rounded-full bg-gray-900 border border-gray-800 text-gray-400 hover:text-white hover:border-orange-500 transition-all duration-300"
            >
              <FaTimes className="h-5 w-5" />
            </button>
          </div>

          {/* Menu Content */}
          <div className="px-4 py-6">
            {/* Quick Actions Grid */}
            <div className="grid grid-cols-2 gap-2 mb-6">
              {[
                { icon: <FaMapMarkerAlt />, text: 'Store Locator', to: '/locate-store' },
                { icon: <FaClock />, text: 'Book Eye Test', to: '/try-at-home' }
              ].map((item, index) => (
                <Link
                  key={index}
                  to={item.to}
                  onClick={() => setIsMenuOpen(false)}
                  className="flex flex-col items-center justify-center gap-1 bg-gray-900/50 border border-gray-800 p-3 rounded-xl hover:border-orange-500 hover:bg-orange-500/10 transition-all duration-300 group"
                >
                  <span className="text-orange-500 text-lg group-hover:scale-110 transition-transform">
                    {item.icon}
                  </span>
                  <span className="text-[10px] xs:text-xs font-medium text-gray-300 group-hover:text-white text-center">
                    {item.text}
                  </span>
                </Link>
              ))}
            </div>

            {/* Categories */}
            <div className="space-y-1 mb-6">
              <p className="text-[10px] xs:text-xs font-semibold text-gray-500 mb-2 px-2 tracking-wider">SHOP BY CATEGORY</p>
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`block px-4 py-3 rounded-xl transition-all duration-300 ${
                    isActive(link.path)
                      ? 'bg-orange-500/10 text-orange-500 border border-orange-500/20'
                      : 'text-gray-300 hover:text-white hover:bg-orange-500/10 border border-transparent hover:border-orange-500/20'
                  }`}
                >
                  <span className="text-sm xs:text-base font-medium">{link.name}</span>
                </Link>
              ))}
            </div>

            {/* Account Links */}
            <div className="space-y-1">
              <p className="text-[10px] xs:text-xs font-semibold text-gray-500 mb-2 px-2 tracking-wider">ACCOUNT</p>
              {[
                { icon: <FaUserPlus />, text: 'Sign Up / Sign In', to: '/signin' },
                { icon: <FaTruck />, text: 'Track Order', to: '/track-order' },
                { icon: <FaMapMarkerAlt />, text: 'Locate Store', to: '/locate-store' },
                { icon: <FaHome />, text: 'Try @ Home', to: '/try-at-home' }
              ].map((item, index) => (
                <Link
                  key={index}
                  to={item.to}
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-300 hover:text-white hover:bg-orange-500/10 border border-transparent hover:border-orange-500/20 transition-all duration-300 group"
                >
                  <span className="text-orange-500 text-base group-hover:scale-110 transition-transform">
                    {item.icon}
                  </span>
                  <span className="text-xs xs:text-sm">{item.text}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes width {
          0% { width: 0; left: 50%; }
          50% { width: 100%; left: 0; }
          100% { width: 0; left: 50%; }
        }
        .animate-spin-slow {
          animation: spin-slow 3s linear infinite;
        }
        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }
        .animate-width {
          animation: width 2s ease-in-out infinite;
        }
        /* Extra small screen utilities */
        @media (min-width: 375px) {
          .xs\\:text-xs { font-size: 0.75rem; }
          .xs\\:text-sm { font-size: 0.875rem; }
          .xs\\:text-base { font-size: 1rem; }
          .xs\\:h-10 { height: 2.5rem; }
          .xs\\:max-w-none { max-width: none; }
          .xs\\:whitespace-normal { white-space: normal; }
        }
        /* Mobile icon compact mode for very small screens (≤360px) */
        @media (max-width: 360px) {
          .mobile-icon {
            padding: 0.375rem !important; /* p-1.5 */
          }
          .mobile-icon svg {
            height: 1rem;
            width: 1rem;
          }
        }
      `}</style>
    </nav>
  );
};

export default Navbar;